import { useOrchestratorBaseUrl } from './useOrchestratorBaseUrl.js'
import { apiErrorMessage } from '@/services/apiErrorMessage.js'

/**
 * Workspace creation composable.
 *
 * `createUploadWorkspace` performs the ADR-023 Phase 3 three-call flow:
 *   1. POST /workspaces/upload           — server creates the workspace entity in
 *                                          status `awaiting_upload` and returns a
 *                                          presigned PUT URL bound to the archive key.
 *   2. PUT  <presigned-url>              — client uploads the archive bytes
 *                                          directly to S3 (no proxy through Lambda
 *                                          — Lambda body is bounded, archives are not).
 *   3. POST /workspaces/{id}/assemble    — server preflights + dispatches the
 *                                          Assembly Lambda to extract the archive.
 *
 * The previous flow (POST /workspaces { upload: true } → POST /workspaces/{id}/upload)
 * relied on a Lambda binary-body upload endpoint that does not exist in the
 * deployed API. That path is gone, not preserved alongside the new one
 * (greenfield rule).
 */
export function useWorkspaceCreation() {
  const { orchestratorBaseUrl, apiHeaders } = useOrchestratorBaseUrl()

  async function requestJson(path, options = {}) {
    const response = await fetch(`${orchestratorBaseUrl.value}${path}`, {
      ...options,
      headers: {
        ...apiHeaders.value,
        ...(options.headers || {}),
      },
    })

    const data = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new Error(apiErrorMessage(data, response.status))
    }

    return data
  }

  /**
   * Upload archive bytes to a presigned S3 URL via PUT. Lambda is NOT in this
   * path — the body goes straight to S3, so it can be larger than the Lambda
   * 6 MB sync limit.
   */
  async function putArchiveToPresignedUrl(uploadUrl, file) {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      // The presigned URL embeds the bucket auth; no extra headers required.
      // Content-Type must match what the server signed when it generated the URL
      // (S3PresignedUrlProvider signs `application/octet-stream`).
      headers: { 'Content-Type': 'application/octet-stream' },
      body: file,
    })
    if (!response.ok) {
      throw new Error(`Upload to S3 failed: HTTP ${response.status}`)
    }
  }

  async function createGithubWorkspace(params) {
    return requestJson('/workspaces', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        source: 'github',
        type: params.visibility === 'private' ? 'private-github' : 'public-github',
        repoUrl: params.repoUrl,
        name: params.name,
        ...(params.description ? { description: params.description } : {}),
        ...(params.branch ? { branch: params.branch } : {}),
        ...(params.visibility === 'private' && params.githubToken
          ? { credentials: { github_token: params.githubToken } }
          : {}),
      }),
    })
  }

  /**
   * Create an upload-source workspace from a user-supplied archive file.
   *
   * @param {{ name: string, description?: string, file: File }} params
   * @returns {Promise<object>} the workspace entity returned by the server
   *   AFTER the assemble dispatch (`status: 'creating'`, plus `taskId` if the
   *   server enqueued a workspace task — the caller can poll for completion
   *   via GET /workspaces/{id}/tasks/{taskId}).
   */
  async function createUploadWorkspace(params) {
    const created = await requestJson('/workspaces/upload', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: params.name,
        archiveFilename: params.file.name,
        archiveSize: params.file.size,
        ...(params.description ? { description: params.description } : {}),
      }),
    })

    if (!created?.upload?.url) {
      throw new Error('Server response missing upload URL')
    }

    await putArchiveToPresignedUrl(created.upload.url, params.file)

    const assembleResponse = await requestJson(`/workspaces/${created.id}/assemble`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    })

    return {
      ...created,
      status: assembleResponse.status ?? 'creating',
      ...(assembleResponse.taskId ? { taskId: assembleResponse.taskId } : {}),
    }
  }

  return {
    createGithubWorkspace,
    createUploadWorkspace,
  }
}
